import React, { useState, useMemo } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';

const EndpointDirectory = ({ endpoints }) => {
    const [searchTerm, setSearchTerm] = useState('');

    // Filter endpoints based on search term
    const filteredEndpoints = useMemo(() => {
        if (!searchTerm) return endpoints;
        
        return endpoints.filter(endpoint => 
            endpoint.path.toLowerCase().includes(searchTerm.toLowerCase()) ||
            endpoint.methods.some(method => 
                method.toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [endpoints, searchTerm]);

    const getMethodBadgeColor = (method) => {
        const colors = {
            'GET': 'bg-green-600',
            'POST': 'bg-blue-600',
            'PUT': 'bg-yellow-600',
            'PATCH': 'bg-orange-600',
            'DELETE': 'bg-red-600',
            'OPTIONS': 'bg-purple-600',
            'HEAD': 'bg-gray-600'
        };
        return colors[method] || 'bg-gray-600';
    };

    const getRateLimitStatus = (endpoint) => {
        if (endpoint.rateLimited === false) {
            return { text: 'Unlimited', color: 'text-green-500', bg: 'bg-green-200 dark:bg-green-900/10' };
        }
        
        if (endpoint.priority === 'HIGH') {
            return { text: 'Rate Limited', color: 'text-red-500', bg: 'bg-red-200 dark:bg-red-900/20' };
        }
        
        if (endpoint.priority === 'MEDIUM') {
            return { text: 'Rate Limited', color: 'text-yellow-500', bg: 'bg-yellow-200 dark:bg-yellow-900/20' };
        }
        
        return { text: 'Rate Limited', color: 'text-orange-500', bg: 'bg-orange-400 dark:bg-orange-900/10' };
    };

    if (!endpoints || endpoints.length === 0) {
        return (
            <div className="rounded">
                <h4 className="font-semibold mb-3 text-font-primary">API Endpoints Directory</h4>
                <div className="text-center py-8">
                    <Icon icon="mdi:api-off" className="text-4xl text-font-secondary mb-2" />
                    <p className="text-font-secondary">No endpoints available</p>
                </div>
            </div>
        );
    }

    return (
        <div className="rounded flex flex-col gap-3 h-[600px]">
            <h4 className="font-semibold text-font-primary flex items-center gap-1 flex-shrink-0 text-base">
                <Icon icon="icon-park-solid:endpoint-displacement" className="text-xl text-primary" />
                API Endpoints Directory
            </h4>

            <hr className='border-border' />
            
            {/* Search Bar */}
            <div className="relative flex-shrink-0">
                <Icon 
                    icon="mingcute:search-line" 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-primary text-lg" 
                />
                <input
                    type="text"
                    placeholder="Search endpoints by path or method..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 input-textbox rounded text-sm border border-border focus:border-primary focus:outline-none"
                />
            </div>

            {/* Scrollable Endpoints Table Container */}
            <div className="card-container bg-background px-2 py-1 flex-1 overflow-hidden min-h-0">
                <div className="h-full overflow-y-auto">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 z-10">
                            <tr className="border-b px-2 py-1 bg-background border-border">
                                <th className="text-left py-2 px-3 font-semibold text-font-primary">Path</th>
                                <th className="text-left py-2 px-3 font-semibold text-font-primary">Methods</th>
                                <th className="text-left py-2 px-3 font-semibold text-font-primary">Rate Limiting</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEndpoints.map((endpoint, index) => {
                                const rateLimitStatus = getRateLimitStatus(endpoint);
                                return (
                                    <tr 
                                        key={index} 
                                        className="border-b border-bgAccent transition-colors"
                                    >
                                        <td className="py-3 px-3">
                                            <div className="flex items-center gap-2">
                                                <code className="text-primary font-semibold font-mono text-sm">
                                                    {endpoint.path}
                                                </code>
                                                {endpoint.category && (
                                                    <span className="text-xs text-font-secondary bg-bgAccent px-2 py-1 rounded">
                                                        {endpoint.category}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-3 px-3">
                                            <div className="flex flex-row flex-wrap gap-2">
                                                {endpoint.methods.map((method, methodIndex) => (
                                                    <span
                                                        key={methodIndex}
                                                        className={`px-2 py-1 text-white rounded text-xs font-medium ${getMethodBadgeColor(method)}`}
                                                    >
                                                        {method}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="py-3 px-3">
                                            <div className="flex items-center gap-2">
                                                <div className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap truncate ${rateLimitStatus.bg} ${rateLimitStatus.color}`}>
                                                    {rateLimitStatus.text}
                                                </div>
                                                {endpoint.rateLimited && (
                                                    <Icon icon="mdi:shield-alert" className="text-orange-500 text-sm" />
                                                )}
                                                {!endpoint.rateLimited && (
                                                    <Icon icon="mdi:shield-check" className="text-green-500 text-sm" />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Results Summary */}
            <div className="pt-3 border-t border-bgAccent flex-shrink-0">
                <p className="text-xs text-font-secondary">
                    Showing {filteredEndpoints.length} of {endpoints.length} endpoints
                    {searchTerm && ` matching "${searchTerm}"`}
                </p>
            </div>
        </div>
    );
};

export default EndpointDirectory;
